import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { Table, Divider, Row, Col, Tag, Button, Popconfirm, Tooltip } from 'antd';
import { getSortState, JhiItemCount, JhiPagination, TextFormat } from 'react-jhipster';
import { APP_TIMESTAMP_FORMAT, APP_LOCAL_DATE_FORMAT, AUTHORITIES } from 'app/config/constants';
import { getElevePointages, deleteAbsenceEleve } from './eleve.reducer';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { ITEMS_PER_PAGE } from 'app/shared/util/pagination.constants';
import { RouteComponentProps } from 'react-router-dom';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { DeleteOutlined } from '@ant-design/icons';

interface IPointagesAbsencesTab {
  props: RouteComponentProps<{ id: string; ecoleid: string }>;
  eleveId: string;
  isActive: boolean;
  anneeScolaireId: string;
}

const PointagesAbsencesTab: React.FC<IPointagesAbsencesTab> = ({ eleveId, isActive, anneeScolaireId, props }) => {
  const dispatch = useAppDispatch();
  const { entitiesPointages: pointages, loading, updating } = useAppSelector(state => state.eleve);

  const eleve = useAppSelector(state => state.eleve.entity);

  // Vérification des permissions admin pour la suppression d'absence
  const canDeleteAbsence = useAppSelector(state =>
    hasAnyAuthority(state.authentication.account.authorities, [
      AUTHORITIES.ADMIN,
      AUTHORITIES.DIRECTEUR,
      AUTHORITIES.DIRECTEUR_ETUDE,
      AUTHORITIES.SURVEILLANT_GENERAL,
    ])
  );

  const handleDeleteAbsence = (absenceId: string) => {
    dispatch(deleteAbsenceEleve({ absenceId, eleveId, anneeScolaireId }));
  };

  // Vérifie si le cours est terminé (date + heure de fin dépassées)
  const isCoursTermine = (absenceDate: string, heureFin: string): boolean => {
    if (!absenceDate || !heureFin) return true; // Par défaut, considérer comme terminé si données manquantes
    try {
      const dateAbsence = new Date(absenceDate);
      const [heures, minutes] = heureFin.split(':').map(Number);
      dateAbsence.setHours(heures, minutes, 0, 0);
      return new Date() > dateAbsence;
    } catch {
      return true;
    }
  };

  const totalItems = useAppSelector(state => state.eleve.totalPointages);
  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getSortState(props.location, ITEMS_PER_PAGE, 'id'), props.location.search)
  );

  useEffect(() => {
    if (isActive) {
      dispatch(getElevePointages({ eleveId, anneeScolaireId, page: paginationState.activePage - 1, size: paginationState.itemsPerPage }));
    }
  }, [dispatch, eleveId, isActive, paginationState]);

  const handlePagination = (currentPage: number) => {
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });
  };

  const pointagesColumns = [
    {
      title: 'Pointages',
      key: 'type',
      dataIndex: 'type',
      render: type =>
        type === 'ENTREE' || type === 'ENTREE_BUS' ? (
          <Tag color="green">{type === 'ENTREE' ? type : 'ENTREE BUS'}</Tag>
        ) : (
          <Tag color="red">{type === 'SORTIE' ? type : 'SORTIE BUS'}</Tag>
        ),
    },
    {
      title: 'Date & Heures',
      key: 'date',
      dataIndex: 'date',
      render: date => <TextFormat type="date" value={date} format={APP_TIMESTAMP_FORMAT} />,
    },
  ];

  const absencesColumns = [
    { title: 'Cours', key: 'cours', dataIndex: 'cours' },
    {
      title: 'Date',
      key: 'date',
      dataIndex: 'date',
      render: date => <TextFormat type="date" value={date} format={APP_LOCAL_DATE_FORMAT} />,
    },
    ...(canDeleteAbsence
      ? [
          {
            title: 'Action',
            key: 'action',
            render: (_, record: { id: string; coursTermine: boolean }) =>
              record.coursTermine ? (
                <Tooltip title="Impossible de supprimer : le cours est terminé">
                  <Button type="text" icon={<DeleteOutlined />} size="small" disabled />
                </Tooltip>
              ) : (
                <Popconfirm
                  title="Supprimer cette absence ?"
                  onConfirm={() => handleDeleteAbsence(record.id)}
                  okText="Supprimer"
                  cancelText="Annuler"
                  okButtonProps={{ danger: true, loading: updating }}
                >
                  <Button type="text" danger icon={<DeleteOutlined />} size="small" />
                </Popconfirm>
              ),
          },
        ]
      : []),
  ];

  const pointagesData = pointages?.map(p => ({ type: p.type, date: p.date }));
  const absencesData = eleve.absences
    ?.map(a => ({
      key: a.id,
      id: a.id,
      cours: a.calendarEntry ? `${a.calendarEntry.matiere.nom}, ${a.calendarEntry.heureDebut}-${a.calendarEntry.heureFin}` : '',
      date: a.date,
      coursTermine: isCoursTermine(a.date, a.calendarEntry?.heureFin),
    }))
    ?.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  return (
    <Row className="justify-content-center">
      <Col lg={{ span: 10 }} xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 12 }}>
        <h5>Historique Pointages</h5>
        <Table columns={pointagesColumns} dataSource={pointagesData} bordered pagination={false} loading={loading} />
        <div className={pointages && pointages.length > 0 ? 'table-responsive' : 'd-none'}>
          <Row className="justify-content-center">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </Row>
          <Row className="justify-content-center">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </Row>
        </div>
      </Col>
      <Divider type="vertical" style={{ height: 'auto' }} />
      <Col lg={{ span: 10 }} xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 12 }}>
        <h5>Historique Absences</h5>
        <Table columns={absencesColumns} dataSource={absencesData} bordered pagination={false} />
      </Col>
    </Row>
  );
};

export default PointagesAbsencesTab;
